import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React from 'react';
import {
  type LoaderFunctionArgs,
  redirect,
  useLocation,
  useNavigate,
} from 'react-router';

import type { JurisdictionConfig } from '@dnc/baseline/contentful/types';
import {
  Jurisdictions,
  type Jurisdiction,
} from '@dnc/baseline/data/jurisdictions';
import { DEFAULT_LOCALES } from '@dnc/baseline/utils/localization';
import type { Option } from '@dnc/baseline/utils/option';

import { Services } from '../../services/services';
import { UrlHelper } from '../../services/url-helper';
import { parseQueryString } from '../../services/utils';
import { AnalyticsProvider } from '../AnalyticsProvider';
import { StateConfirmation } from '../StateConfirmation';

import { Page } from './page';

const VepBackup = React.lazy(() =>
  import('./vepbackup').then(({ VepBackup }) => ({ default: VepBackup }))
);

/**
 * Wrapper around {@link Page} that:
 *
 * - Sets up a {@link AnalyticsProvider} and passes it the props to keep it
 *   updated.
 * - Loads sitewide alerts via a react-query hook.
 * - Makes a {@link JURISDICTION_CONTEXT} `Provider`.
 */
export const ConnectedPage: React.FunctionComponent<
  {
    services: Services;
    jurisdictionConfig: Option<JurisdictionConfig>;
  } & Omit<
    React.ComponentProps<typeof Page>,
    | 'sitewideAlerts'
    | 'jurisdictionAlerts'
    | 'supportedLocales'
    | 'priorityLocales'
    | 'customVoterHotline'
    | 'hotlineFooterOverride'
  >
> = ({ children, services, jurisdiction, jurisdictionConfig, ...props }) => {
  const location = useLocation();
  const urlHelper = UrlHelper.fromLocation(location);
  const contentfulParams = urlHelper.parseContentfulParams();

  // We load these via useQuery so that we don’t block first render on getting
  // them.
  const sitewideAlerts = useQuery(
    services.voterEducation.sitewideAlertsQueryOptions(
      !!contentfulParams.preview
    )
  );

  return (
    <AnalyticsProvider
      analytics={services.analytics}
      location={location}
      jurisdiction={jurisdiction}
    >
      <Page
        jurisdiction={jurisdiction}
        sitewideAlerts={sitewideAlerts.data?.sitewideAlerts}
        jurisdictionAlerts={jurisdictionConfig?.jurisdictionAlert}
        supportedLocales={
          jurisdictionConfig?.supportedLocales ?? DEFAULT_LOCALES
        }
        priorityLocales={jurisdictionConfig?.priorityLocales ?? DEFAULT_LOCALES}
        customVoterHotline={jurisdictionConfig?.voterHotline}
        hotlineFooterOverride={jurisdictionConfig?.hotlineFooterOverride}
        {...props}
      >
        {children}
      </Page>
    </AnalyticsProvider>
  );
};

/**
 * Enforces that the ?lang= parameter is correct, throwing a redirect if
 * necessary.
 *
 * The two checks are:
 * - Is the locale one of our supported ActiveLocales?
 * - If there’s a current Jurisdiction, is the locale supported by the
 *   jurisdiction?
 *
 * If either of those fails, redirects to the same URL but w/o the lang
 * parameter, which will cause the app to fall back to the defaultHostLocale.
 */
function enforceLocale(
  urlString: string,
  jurisdictionConfig: Option<JurisdictionConfig>
) {
  const urlHelper = UrlHelper.fromRequestUrl(urlString);

  if (urlHelper.siteParams[UrlHelper.LOCALE_PARAM]) {
    const locale = urlHelper.getLocale();

    if (locale === null) {
      // This means that someone provided a "lang" parameter that we couldn’t
      // parse as an ActiveLocale. Redirect to remove it.

      throw redirect(
        UrlHelper.changeRequestUrlParams(urlString, {
          [UrlHelper.LOCALE_PARAM]: undefined,
        })
      );
    }

    if (
      jurisdictionConfig &&
      !jurisdictionConfig.supportedLocales.includes(locale)
    ) {
      // query parameter is a locale we understand, but it’s not valid for the
      // current jurisdiction, so remove it
      throw redirect(
        UrlHelper.changeRequestUrlParams(urlString, {
          [UrlHelper.LOCALE_PARAM]: undefined,
        })
      );
    }
  }
}

/**
 * Private helper for loading a {@link JurisdictionConfig}.
 *
 * Throws if the loading failed.
 */
async function fetchJurisdictionConfig(
  services: Services,
  url: string,
  jurisdiction: Jurisdiction
): Promise<JurisdictionConfig> {
  const today = moment();
  const urlHelper = UrlHelper.fromRequestUrl(url);
  const contentfulParams = urlHelper.parseContentfulParams();

  const jurisdictionConfig =
    await services.voterEducation.fetchJurisdictionConfig(
      jurisdiction,
      !!contentfulParams.preview,
      !!contentfulParams.test_config,
      today
    );

  if (!jurisdictionConfig) {
    throw new Error(`Failure loading jurisdiction: ${jurisdiction}`);
  }

  return jurisdictionConfig;
}

export type RequiredLoadedJurisdictionInfo = {
  jurisdiction: Jurisdiction;
  jurisdictionConfig: JurisdictionConfig;
};

/**
 * Determines the jurisdiction and loads the jurisdictionConfig from the
 * "jurisdiction" path parameter.
 *
 * Used for the "/votinginfo/:jurisdiction" route.
 *
 * Redirects to '/' if the value of `:jurisdiction` isn’t a proper Jurisdiction,
 * errors if the {@link JurisdictionConfig} didn’t load, so this loader is
 * guaranteed to provide a value for both `jurisdiction` and
 * `jurisdictionConfig`.
 */
export async function loadJurisdictionFromPathParams(
  services: Services,
  { request, params }: LoaderFunctionArgs
): Promise<RequiredLoadedJurisdictionInfo> {
  const jurisdiction = params['jurisdiction']!;

  if (!Jurisdictions.isJurisdiction(jurisdiction)) {
    throw redirect('/');
  }

  const jurisdictionConfig = await fetchJurisdictionConfig(
    services,
    request.url,
    jurisdiction
  );

  enforceLocale(request.url, jurisdictionConfig);

  return {
    jurisdiction,
    jurisdictionConfig,
  };
}

export type OptionalLoadedJurisdictionInfo = {
  jurisdiction: Option<Jurisdiction>;
  jurisdictionConfig: Option<JurisdictionConfig>;
};

/**
 * Determines the jurisdiction and loads the jurisdictionConfig from the "state"
 * query parameter. If it’s not provided, uses {@link JurisdictionService} to
 * look it up via geo-ip.
 *
 * If the value of the `state` parameter is not a valid jurisdiction, redirects
 * to the same URL, but without a `state` parameter.
 *
 * Respects a secret `no_geo` query parameter that prevents it from loading
 * geo-ip data, for easier testing of those cases.
 *
 * This may return a `jurisdiction` but not a `jurisdictionConfig` if there was
 * an error loading from Contentful.
 */
export async function loadJurisdictionFromSearchQuery(
  services: Services,
  { request }: LoaderFunctionArgs
): Promise<OptionalLoadedJurisdictionInfo> {
  const jurisdiction = await jurisdictionFromRequestParams(request, services);

  let jurisdictionConfig = undefined;
  try {
    jurisdictionConfig =
      jurisdiction &&
      (await fetchJurisdictionConfig(services, request.url, jurisdiction));
  } catch (error: any) {
    console.error(`Failure loading jurisdiction: ${jurisdiction}`, error);
    // Log the error in Sentry
    services.sentry.record(error);
  }

  enforceLocale(request.url, jurisdictionConfig);

  return {
    jurisdiction,
    jurisdictionConfig,
  };
}

/**
 * Returns either the "state" parameter from the query parameters, or uses geo
 * IP to look up the user’s state.
 *
 * Will throw a redirect object if `state` is provided but is not a valid state
 * code.
 */
export async function jurisdictionFromRequestParams(
  request: Request,
  services: Services
): Promise<Option<Jurisdiction>> {
  const url = new URL(request.url);
  const queryParams = parseQueryString(url.search);

  if (queryParams[UrlHelper.JURISDICTION_PARAM]) {
    const stateParam = queryParams[UrlHelper.JURISDICTION_PARAM]!.toUpperCase();

    if (Jurisdictions.isJurisdiction(stateParam)) {
      return stateParam;
    } else {
      // "state" parameter is invalid, so remove it and redirect.
      throw redirect(
        UrlHelper.changeRequestUrlParams(request.url, { state: undefined })
      );
    }
  } else if (!queryParams[UrlHelper.NO_GEO_PARAM]) {
    // No state param, so try geo-ip
    return await services.jurisdiction.getJurisdiction();
  } else {
    return undefined;
  }
}

/**
 * Enforces that a jurisdiction has been selected and a config has been loaded.
 *
 * If the former is not true, renders a {@link StateConfirmation} instead of
 * children. If the latter is not true, renders the VEP Widget (which uses VEP
 * API to get its data).
 *
 * Otherwise it calls its child function with the `jurisdiction` and
 * `jurisdictionConfig`.
 *
 * When the user selects a jurisdiction via this component, the selection is set
 * by doing a history replace with the new jurisdiction in the `state` query
 * parameter.
 */
export const EnforceJurisdictionSelection: React.FunctionComponent<{
  jurisdiction: Option<Jurisdiction>;
  jurisdictionConfig: Option<JurisdictionConfig>;
  children: (props: {
    jurisdiction: Jurisdiction;
    jurisdictionConfig: JurisdictionConfig;
  }) => React.ReactNode;
}> = ({ jurisdiction, jurisdictionConfig, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const updateJurisdiction = (j: Jurisdiction) => {
    navigate(
      UrlHelper.changeLocationParams(location, {
        [UrlHelper.JURISDICTION_PARAM]: j,
      }),
      {
        replace: true,
        preventScrollReset: true,
      }
    );
  };

  if (!jurisdiction) {
    return (
      <StateConfirmation
        jurisdiction={jurisdiction}
        updateJurisdiction={updateJurisdiction}
      />
    );
  } else if (!jurisdictionConfig) {
    // Having jurisdiction but no jurisdictionConfig is an indication that the
    // loader was unable to get info from Contentful. Since this is rare, we
    // lazy-load the <VepBackup> component so we don’t have to include it in the
    // complete bundle.
    return (
      <React.Suspense fallback={<div className="loading-spinner" />}>
        <VepBackup
          jurisdiction={jurisdiction}
          updateJurisdiction={updateJurisdiction}
        />
      </React.Suspense>
    );
  } else {
    return children({ jurisdiction, jurisdictionConfig });
  }
};
